/**
 * Create room — provision a new room in the current tenant. The backend creates
 * it as `provisioning` (status isn't set here). Tenant is prefilled (read-only)
 * from the list context; name is required.
 */

import { useState } from 'react';
import styled from 'styled-components';
import Button from '@8x8/oxygen-button';
import TextField from '@8x8/oxygen-text-field';
import { createRoom } from '../api';
import type { SessionState } from '../api';
import { navigate } from '../router';
import { useToast } from '../toast';
import { Crumbs } from '../ui/Crumbs';
import { FieldHint, FieldLabel, Notice, RequiredMark } from '../ui/primitives';

const Page = styled.div`
    padding: 16px 24px 40px;
`;

const FormCard = styled.form`
    max-width: 640px;
    background: ${p => p.theme.ui06};
    border: 1px solid ${p => p.theme.ui01};
    border-radius: 8px;
    padding: 24px;
`;

const CardTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${p => p.theme.textColor01};
`;

const CardSub = styled.div`
    font-size: 13px;
    color: ${p => p.theme.textColor02};
    margin-top: 2px;
`;

export function RoomCreate({ session }: { session: SessionState }): JSX.Element {
    // The room is created in the operator's own tenant (their 8x8 token's cid).
    const tenant = session.tenant ?? '';
    const toast = useToast();

    const [name, setName] = useState('');
    const [country, setCountry] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const backToList = (): void => navigate('/rooms');

    const onSubmit = async (ev: React.FormEvent): Promise<void> => {
        ev.preventDefault();
        setError(null);

        if (name.trim().length === 0 || tenant.trim().length === 0) {
            setError('Name and tenant are required.');

            return;
        }

        setBusy(true);

        const result = await createRoom({
            name: name.trim(),
            tenant: tenant.trim(),
            ...(country.trim() ? { countryCode: country.trim() } : {})
        });

        if (!result.ok) {
            setBusy(false);
            setError(`Couldn't create room: ${result.message}`);

            return;
        }

        toast.notify('success', 'Room created.');
        navigate(`/rooms/${encodeURIComponent(result.data.id)}`, true);
    };

    return (
        <Page>
            <Crumbs items={[{ label: 'Rooms', to: '/rooms' }, { label: 'New room' }]} />

            <FormCard onSubmit={onSubmit}>
                <CardTitle>Add new room</CardTitle>
                <CardSub>Create and configure a new room in this tenant. It starts as provisioning.</CardSub>

                {error ? (
                    <div style={{ marginTop: 18 }}>
                        <Notice kind="warning">{error}</Notice>
                    </div>
                ) : null}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 22 }}>
                    <div>
                        <TextField
                            label="Room name"
                            isRequired
                            value={name}
                            placeholder="Lisbon — Ocean Room"
                            fullWidth
                            hasError={Boolean(error) && name.trim().length === 0}
                            autoFocus
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                        />
                    </div>
                    <div>
                        <FieldLabel><RequiredMark>*</RequiredMark> Tenant</FieldLabel>
                        <TextField value={tenant} isReadOnly fullWidth onChange={() => undefined} />
                        <FieldHint>Which 8x8 customer this room belongs to.</FieldHint>
                    </div>
                    <div style={{ maxWidth: 200 }}>
                        <TextField
                            label="Country code"
                            value={country}
                            placeholder="PT"
                            fullWidth
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCountry(e.target.value)}
                        />
                        <FieldHint>ISO 3166-1 alpha-2.</FieldHint>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
                    <Button type="button" variant="secondary" onClick={backToList}>Cancel</Button>
                    <Button type="submit" variant="primary" isDisabled={busy}>
                        {busy ? 'Creating…' : 'Create room'}
                    </Button>
                </div>
            </FormCard>
        </Page>
    );
}
